/**
 * Reporting periods and the deadlines that hang off them.
 *
 *  - The UVA (Umsatzsteuervoranmeldung) is filed monthly or quarterly and is due on the 15th of
 *    the second month after the period (§ 21 Abs 1 UStG): January is due 15 March, Q1 is due
 *    15 May. Businesses below 100.000 € turnover may file quarterly.
 *  - Records must be kept for seven years counted from the end of the calendar year they belong
 *    to (§ 132 BAO), so a 2026 receipt may be thrown away in 2034.
 */

export type VatPeriodKind = "monthly" | "quarterly" | "none";

export type Period = {
  /** Stable identifier, e.g. "2026-M03" or "2026-Q1". */
  key: string;
  kind: VatPeriodKind;
  year: number;
  /** 1-12 for months, 1-4 for quarters. */
  index: number;
  /** Inclusive ISO dates. */
  from: string;
  to: string;
};

const pad = (value: number) => String(value).padStart(2, "0");
const iso = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`;

export function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function monthPeriod(year: number, month: number): Period {
  return {
    key: `${year}-M${pad(month)}`,
    kind: "monthly",
    year,
    index: month,
    from: iso(year, month, 1),
    to: iso(year, month, lastDayOfMonth(year, month)),
  };
}

export function quarterPeriod(year: number, quarter: number): Period {
  const firstMonth = (quarter - 1) * 3 + 1;
  const lastMonth = firstMonth + 2;
  return {
    key: `${year}-Q${quarter}`,
    kind: "quarterly",
    year,
    index: quarter,
    from: iso(year, firstMonth, 1),
    to: iso(year, lastMonth, lastDayOfMonth(year, lastMonth)),
  };
}

export function yearPeriod(year: number): Period {
  return {
    key: `${year}`,
    kind: "none",
    year,
    index: 1,
    from: iso(year, 1, 1),
    to: iso(year, 12, 31),
  };
}

/** Every filing period of a calendar year, newest last. "none" yields the year as one period. */
export function periodsForYear(year: number, kind: VatPeriodKind): Period[] {
  if (kind === "monthly") {
    return Array.from({ length: 12 }, (_, index) => monthPeriod(year, index + 1));
  }
  if (kind === "quarterly") {
    return Array.from({ length: 4 }, (_, index) => quarterPeriod(year, index + 1));
  }
  return [yearPeriod(year)];
}

/** The filing period an ISO date falls into. */
export function periodFor(date: string, kind: VatPeriodKind): Period | null {
  const match = /^(\d{4})-(\d{2})/.exec(date);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (kind === "monthly") return monthPeriod(year, month);
  if (kind === "quarterly") return quarterPeriod(year, Math.ceil(month / 3));
  return yearPeriod(year);
}

/** Inclusive test, on ISO dates so no time zone can shift a booking into the wrong month. */
export function isWithin(date: string | undefined, period: Period): boolean {
  if (!date) return false;
  const day = date.slice(0, 10);
  return day >= period.from && day <= period.to;
}

/** The 15th of the second month after the period (§ 21 Abs 1 UStG). */
export function uvaDueDate(period: Period): string {
  const [year, month] = period.to.split("-").map(Number) as [number, number, number];
  const dueMonth = month + 2;
  return dueMonth > 12 ? iso(year + 1, dueMonth - 12, 15) : iso(year, dueMonth, 15);
}

/** Records may be destroyed after seven years counted from the end of their year (§ 132 BAO). */
export function retentionUntil(date: string | undefined): string | null {
  const match = /^(\d{4})/.exec(date ?? "");
  if (!match) return null;
  return iso(Number(match[1]) + 7, 12, 31);
}

/** The business year, which may start in a month other than January. */
export function fiscalYear(year: number, startMonth = 1): { from: string; to: string } {
  if (startMonth === 1) return { from: iso(year, 1, 1), to: iso(year, 12, 31) };
  const endMonth = startMonth - 1;
  return {
    from: iso(year, startMonth, 1),
    to: iso(year + 1, endMonth, lastDayOfMonth(year + 1, endMonth)),
  };
}

/** The period before this one. */
export function previousPeriod(period: Period): Period {
  if (period.kind === "monthly") {
    return period.index === 1
      ? monthPeriod(period.year - 1, 12)
      : monthPeriod(period.year, period.index - 1);
  }
  if (period.kind === "quarterly") {
    return period.index === 1
      ? quarterPeriod(period.year - 1, 4)
      : quarterPeriod(period.year, period.index - 1);
  }
  return yearPeriod(period.year - 1);
}

/** The period after this one. */
export function nextPeriod(period: Period): Period {
  if (period.kind === "monthly") {
    return period.index === 12
      ? monthPeriod(period.year + 1, 1)
      : monthPeriod(period.year, period.index + 1);
  }
  if (period.kind === "quarterly") {
    return period.index === 4
      ? quarterPeriod(period.year + 1, 1)
      : quarterPeriod(period.year, period.index + 1);
  }
  return yearPeriod(period.year + 1);
}

/** Any range of days, e.g. a card statement's 20th to 19th. Index 0 marks it as custom. */
export function customPeriod(from: string, to: string): Period {
  const [start, end] = from <= to ? [from, to] : [to, from];
  return {
    key: `${start}_${end}`,
    kind: "none",
    year: Number(start.slice(0, 4)),
    index: 0,
    from: start,
    to: end,
  };
}

/** Reads a `Period.key` back - "2026-M09", "2026-Q3", "2026" or "2026-08-20_2026-09-19". */
export function parsePeriodKey(key: string | null | undefined): Period | null {
  if (!key) return null;
  let match = /^(\d{4})-M(\d{2})$/.exec(key);
  if (match) {
    const month = Number(match[2]);
    return month >= 1 && month <= 12 ? monthPeriod(Number(match[1]), month) : null;
  }
  match = /^(\d{4})-Q([1-4])$/.exec(key);
  if (match) return quarterPeriod(Number(match[1]), Number(match[2]));
  if (/^\d{4}$/.test(key)) return yearPeriod(Number(key));
  match = /^(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})$/.exec(key);
  if (match && !Number.isNaN(Date.parse(match[1]!)) && !Number.isNaN(Date.parse(match[2]!))) {
    return customPeriod(match[1]!, match[2]!);
  }
  return null;
}

/**
 * The filing that is actually due: on 12 April you are not preparing April, you are finishing
 * March (or Q1). Returns the most recent period that has already ended.
 */
export function openFilingPeriod(today: string, kind: VatPeriodKind): Period {
  const current = periodFor(today, kind) ?? yearPeriod(Number(today.slice(0, 4)));
  return previousPeriod(current);
}

/** Whole days from today to an ISO date; negative once the date has passed. */
export function daysUntil(date: string, today = new Date().toISOString().slice(0, 10)): number {
  const target = Date.parse(`${date}T00:00:00Z`);
  const from = Date.parse(`${today.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(target) || Number.isNaN(from)) return 0;
  return Math.round((target - from) / 86_400_000);
}
